import { Box, Divider, ListItemText, Menu, MenuItem, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import DropdownTrigger from "@/components/common/DropdownTrigger";
import { getFlagPath, useLanguagePickerModel } from "@/components/common/languagePickerShared";
import type { LanguageOption } from "@/i18n/languageConfig";

const Flag = ({ option, locale }: { option: LanguageOption; locale: string }) => {
  const { t } = useTranslation();
  const flagPath = getFlagPath(option, locale);

  return (
    <Box
      component="img"
      src={flagPath}
      alt={t("common.languagePicker.text.flag", { optionDisplay: option.display })}
      sx={{
        width: 20,
        height: 14,
        display: "block",
        marginInlineStart: 0.5,
        borderRadius: "3px",
        border: "0.5px solid rgba(15, 23, 42, 0.12)",
        objectFit: "cover",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
      }}
    />
  );
};

interface LanguagePickerProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onToggle: (event: React.MouseEvent<HTMLElement>) => void;
}

const LanguagePicker: React.FC<LanguagePickerProps> = ({ anchorEl, onClose, onToggle }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const menuHorizontalOrigin = isRtl ? "left" : "right";
  const {
    availableLanguages,
    browserLocale,
    changeLanguage,
    currentLanguage,
    currentLanguageCode,
    getOptionLabel,
  } = useLanguagePickerModel();
  const handleLanguageChange = React.useCallback(
    async (code: string) => {
      await changeLanguage(code);
      onClose();
    },
    [changeLanguage, onClose],
  );

  const menuEntries = useMemo(
    () =>
      availableLanguages.flatMap((option, index) => {
        const item = (
          <MenuItem
            key={option.code}
            selected={option.code === currentLanguageCode}
            onClick={() => void handleLanguageChange(option.code)}
          >
            <Flag option={option} locale={browserLocale} />
            <ListItemText
              primary={
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  {getOptionLabel(option)}
                </Typography>
              }
            />
          </MenuItem>
        );

        if (index === 0 && availableLanguages.length > 1) {
          return [item, <Divider key="detected-language-divider" sx={{ my: 0.5 }} />];
        }

        return [item];
      }),
    [availableLanguages, browserLocale, currentLanguageCode, getOptionLabel, handleLanguageChange],
  );

  if (!currentLanguage) {
    return null;
  }

  return (
    <>
      <DropdownTrigger isOpen={Boolean(anchorEl)} menuId="language-picker-menu" onClick={onToggle}>
        <Flag option={currentLanguage} locale={browserLocale} />
        <Typography
          variant="body2"
          sx={{
            color: "text.primary",
            fontSize: "13px",
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {getOptionLabel(currentLanguage)}
        </Typography>
      </DropdownTrigger>

      <Menu
        anchorEl={anchorEl}
        id="language-picker-menu"
        open={Boolean(anchorEl)}
        onClose={onClose}
        sx={{ zIndex: theme.zIndex.modal + 2 }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: menuHorizontalOrigin,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: menuHorizontalOrigin,
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 1,
              minWidth: 220,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              boxShadow: 8,
              "& .MuiMenuItem-root": {
                px: 1.5,
                alignItems: "center",
                gap: 1.25,
              },
            },
          },
        }}
      >
        {menuEntries}
      </Menu>
    </>
  );
};

export default LanguagePicker;
